/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { getError, showPrice } from 'utils/appHelpers';
import { collectProductInfo } from 'utils/projectHelpers';
import { useAlert } from 'react-alert';

// Components
import { ArrowLeftIcon, LogoMediumIcon } from 'shared/components/Icons';
import { ProductImageBlock } from 'shared/components/likiee/ProductImageBlock';
import { API, graphqlOperation } from 'aws-amplify';
import { getUserProjectLikiee, likieeByURL } from 'graphql/likiees/queries';
import { onUpdateLikiee } from 'graphql/likiees/subscriptions';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import Button from 'shared/components/Button';
import { addLikieeFrom } from 'graphql/likiees/mutations';
import { useDispatch } from 'react-redux';
import { onAddProduct } from '../../actions/products';
import ProductProperties from '../ProductView/components/ProductProperties';
import ProductDescription from '../ProductView/components/ProductDescription';
import ProductInfoTabs from '../ProductView/components/ProductInfoTabs';

const UserProduct = ({ match, history }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const [product, setProduct] = useState();
  const [isOnMyList, setIsOnMyList] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const projectId = match.params?.projectId;
  const productId = match.params?.productId;

  const getProduct = async () => {
    try {
      setIsLoadingProduct(true);
      const body = { likieeId: productId, projectId };
      const { data } = await API.graphql(graphqlOperation(getUserProjectLikiee, body));
      setProduct(data.getUserProjectLikiee);
      checkLikieeByUrl(data.getUserProjectLikiee);
    } catch (err) {
      alert.error(getError(err));
    } finally {
      setIsLoadingProduct(false);
    }
  };
  const onSaveLikiee = async () => {
    try {
      const input = { id: product.id };
      const addedLikee = await API.graphql(graphqlOperation(addLikieeFrom, input));
      setIsOnMyList(true);
      dispatch(onAddProduct(addedLikee.data.addLikieeFrom));
    } catch (err) {
      alert.error(getError(err));
    }
  };
  const checkLikieeByUrl = async product => {
    try {
      const firstInput = { url: product.url };
      const res = await API.graphql(graphqlOperation(likieeByURL, firstInput));
      setIsOnMyList(!!res?.data?.likieeByURL.items?.length);
    } catch (err) {
      alert.error(getError(err));
    }
  };
  const goBack = () => {
    history.goBack();
  };
  useEffect(() => {
    getProduct();
  }, [productId]);

  // Subscribe to product updates for real-time enhancement status updates
  useEffect(() => {
    let subscription;

    if (product?.id) {
      const setupSubscription = async () => {
        try {
          subscription = API.graphql(graphqlOperation(onUpdateLikiee)).subscribe({
            next: ({ value }) => {
              if (value?.data?.onUpdateLikiee && value.data.onUpdateLikiee.id === product.id) {
                setProduct(prevProduct => ({
                  ...prevProduct,
                  ...value.data.onUpdateLikiee,
                }));
              }
            },
            error: () => {
              // Silently handle subscription errors to avoid disrupting the UI
            },
          });
        } catch (error) {
          // Silently handle subscription setup errors
        }
      };

      setupSubscription();
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [product?.id]);
  if (!product) return <SkeletonLoader type='page' count={4} className='my-20' />;

  // Use default empty objects/arrays for any potentially undefined values
  const {
    images = [],
    mainImage = '',
    favIcon = '',
    name = '',
    description = '',
    sourceName = '',
    properties = {},
  } = collectProductInfo(product);
  return (
    <div className='product-view row pb-15 position-relative'>
      <div className='position-absolute z-index-5 back-btn'>
        <button className='btn p-0' onClick={goBack}>
          <ArrowLeftIcon />
        </button>
      </div>
      <div className='col-md-6 col-12'>
        <ProductImageBlock
          className={'rounded-10 border'}
          sliderClassName='product-view-slider arrows-initial'
          images={images}
          mainImage={mainImage}
          status={product.status}
        />
      </div>
      <div className='col-12 col-md-6 position-relative'>
        <h3 className='mb-8 pr-15 mt-8 mt-md-0'>{name}</h3>
        <div className='mb-7 d-flex align-items-center'>
          <h1 className='mb-0'>{showPrice(product)}</h1>
        </div>
        <ProductDescription
          sourceName={sourceName}
          favIcon={favIcon}
          product={product || {}}
          description={description}
          isLoadingProduct={isLoadingProduct}
        />
        <ProductProperties properties={properties} product={product} />

        {/* Product Information Tabs - Includes AI Insights */}
        <div className='mt-4'>
          <ProductInfoTabs
            sourceName={sourceName}
            favIcon={favIcon}
            product={product || {}}
            description={description}
            properties={properties || {}}
            isLoadingProduct={isLoadingProduct}
            aiSummary={product?.aiSummary}
            reviewsAggregation={product?.reviewsAggregation}
            onAIButtonSuccess={() => {
              // Refresh product data after AI summary is generated
              getProduct();
            }}
          />
        </div>

        <div className='mt-10 mt-md-15'>
          {product.url && (
            <a href={product.affiliateURL || product.url} target='_blank' className='btn btn-primary' rel='noreferrer'>
              {t('btn_visit_product')}
            </a>
          )}
          <Button className='btn-primary ml-8 px-8' onClick={onSaveLikiee} disabled={isOnMyList}>
            <LogoMediumIcon height='18' className='svg-white mr-2' />
            {isOnMyList ? t('btn_added_to_my') : t('btn_add_to_my')}
          </Button>
        </div>
      </div>
    </div>
  );
};
export default UserProduct;
